import { DURATION_CODES, ENERGY_TYPES, getProductFromPriceListUrl, PRODUCT_CONFIG_OPTIONS, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import styles from './ProductCard.module.scss'
import { ProductCardProps } from './types.ts'
import { Button, Card, Heading, Icon, List } from '@boltenergy-be/design-system'
import { formatAmount } from 'utils/format.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import Link from 'components/link/Link.tsx'
import { useTranslation } from 'react-i18next'
import { checkIsDynamicTariffEligible } from 'utils/smartBillingOptions.ts'
import parse from 'html-react-parser'
import { DATE_FORMAT } from 'constants/constants.ts'
import dayjs from 'dayjs'
import { selectPriceLists } from 'store/contact/selectors.ts'
import { isInactiveContract } from 'utils/contracts.ts'
import { checkProductIsRenewable } from 'utils/products.ts'

const ProductCard = ({ product, disabled, pricelists, handleSelectProduct, customerType, currentPriceList }: ProductCardProps) => {
  // Redux
  const serviceContracts = useStoreSelector(selectCurrentServiceContracts)
  const priceListsBreakdown = useStoreSelector(selectPriceLists)

  // i18n
  const { t } = useTranslation('tariffs', { keyPrefix: 'products' })
  const language = useGetLanguage()

  // Constants
  const isInactiveContractPastEndDate =
    isInactiveContract(serviceContracts.electricity) &&
    !!serviceContracts.electricity.detail.effectiveEndDate &&
    dayjs().isAfter(dayjs(serviceContracts.electricity.detail.effectiveEndDate))
  const currentProduct = currentPriceList?.url ? getProductFromPriceListUrl(currentPriceList?.url) : undefined
  const isCurrentProduct = product === currentProduct
  const futureProductPriceList = priceListsBreakdown?.electricity?.future?.find(
    (list) => list?.url && getProductFromPriceListUrl(list.url) === product
  )
  const isPlentyProduct = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.isPlentyProduct]
  const minimumDuration =
    PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode] !== DURATION_CODES.unlimited
      ? Number(PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode]) / 12
      : null
  const isDynamicPricingEligible = checkIsDynamicTariffEligible({ serviceContracts, product })
  const currentProductPriceList = priceListsBreakdown?.electricity?.current
  const newProductPriceList = pricelists[ENERGY_TYPES.electricity]
  const isRenewable = checkProductIsRenewable({
    currentProduct,
    currentPriceList: futureProductPriceList || currentProductPriceList,
    newProduct: product,
    newPriceList: newProductPriceList
  })

  return (
    <Card as="li" key={product} className={styles.card} data-selected={isCurrentProduct} data-disabled={disabled}>
      {product === currentProduct && priceListsBreakdown?.electricity?.current && (
        <small className={styles.current}>
          {parse(
            t(($) => $.currentPriceList, {
              context: priceListsBreakdown.electricity.current?.fromDate ? 'withDate' : 'withoutDate',
              date: dayjs(priceListsBreakdown.electricity.current?.fromDate).format(DATE_FORMAT),
              interpolation: { escapeValue: false }
            })
          )}
        </small>
      )}

      {product !== currentProduct && futureProductPriceList && (
        <small className={styles.future}>
          {parse(
            t(($) => $.futurePriceList, {
              context: futureProductPriceList?.fromDate ? 'withDate' : 'withoutDate',
              date: dayjs(futureProductPriceList?.fromDate).format(DATE_FORMAT),
              interpolation: { escapeValue: false }
            })
          )}
        </small>
      )}

      <Heading as="h3" variant="h4" className={styles.title}>
        {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.name][language]}

        <span className={styles.badge} data-type={PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.isPlentyProduct] ? 'plenty' : 'bolt'}>
          <Icon name={PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.isPlentyProduct] ? 'plentyLogo' : 'boltLogo'} />
        </span>
      </Heading>

      {/* DETAILS */}
      <dl className={styles['product-details']}>
        <div>
          <dt>{t(($) => $.details.platformCost)}</dt>
          <dd>
            {t(($) => $.details.cost[customerType], {
              cost: formatAmount(PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.platformCost][customerType])
            })}
          </dd>
        </div>

        <div>
          <dt>{t(($) => $.details.instalment)}</dt>
          <dd>{t(($) => $.details[PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.instalmentPeriodicity]])}</dd>
        </div>

        <div>
          <dt>{t(($) => $.details.dynamicPricing)}</dt>
          <dd>{isDynamicPricingEligible ? t(($) => $.details.optional) : t(($) => $.details.notAvailable)}</dd>
        </div>

        <div>
          <dt>{t(($) => $.details.directDebit)}</dt>
          <dd>
            {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.directDebitMandatory]
              ? t(($) => $.details.mandatory)
              : t(($) => $.details.optional)}
          </dd>
        </div>

        <div>
          <dt>{t(($) => $.details.contact)}</dt>
          <dd>
            {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.digitalOnly][PRODUCT_CONFIG_OPTIONS.contactOptions]
              ? t(($) => $.details.digitalOnly)
              : t(($) => $.details.contactPhone)}
          </dd>
        </div>

        <div>
          <dt>{t(($) => $.details.billing)}</dt>
          <dd>
            {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.digitalOnly][PRODUCT_CONFIG_OPTIONS.billingOptions]
              ? t(($) => $.details.digitalOnly)
              : t(($) => $.details.billingPaper)}
          </dd>
        </div>
      </dl>

      {/* PRICE LISTS */}
      <div className="mt-200">
        <Heading as="h4" variant="h6" className="mb-100">
          {t(($) => $.downloads.title)}
        </Heading>

        <ul className="flex-col gap-100">
          <li>
            <Link
              as="a"
              href={
                !pricelists?.[ENERGY_TYPES.electricity].startsWith('https://')
                  ? `https://${pricelists?.[ENERGY_TYPES.electricity]}`
                  : pricelists?.[ENERGY_TYPES.electricity]
              }
              download
              target="_blank"
              trailingIcon="download"
              className={styles.download}
            >
              {t(($) => $.downloads.electricity)}
            </Link>
          </li>
          {!!pricelists[ENERGY_TYPES.gas] && (
            <li>
              <Link
                as="a"
                href={
                  !pricelists?.[ENERGY_TYPES.gas]!.startsWith('https://')
                    ? `https://${pricelists?.[ENERGY_TYPES.gas]}`
                    : pricelists?.[ENERGY_TYPES.gas] || ''
                }
                download
                target="_blank"
                trailingIcon="download"
                className={styles.download}
              >
                {t(($) => $.downloads.gas)}
              </Link>
            </li>
          )}
        </ul>
      </div>

      {/* RESTRICTIONS */}
      {(isPlentyProduct || minimumDuration) && (
        <div className="mt-200">
          <Heading as="h4" variant="h6">
            {t(($) => $.restrictions.title)}
          </Heading>
          <List as="ul" className="mt-200 gap-100">
            {isPlentyProduct ? <li className="text-sm">{t(($) => $.restrictions.plenty)}</li> : <></>}
            {minimumDuration ? (
              <li className="text-sm">
                {t(($) => $.restrictions.duration, {
                  count: minimumDuration
                })}
              </li>
            ) : (
              <></>
            )}
          </List>
        </div>
      )}

      <Button
        trailingIcon={isRenewable ? 'refresh' : 'shuffle'}
        type="button"
        className="mt-auto"
        isFullwidth
        size="medium"
        onClick={() => handleSelectProduct(product)}
        disabled={disabled || (isCurrentProduct && !isRenewable) || isInactiveContractPastEndDate}
      >
        {t(($) => $.action, {
          context: isRenewable ? 'renew' : 'switch'
        })}
      </Button>
    </Card>
  )
}

export default ProductCard
